import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as api from '../api';
import { resolveEventImage } from '../utils/eventImage';
import { resizeImage } from '../utils/resizeImage';
import './Admin.css';

const Admin = () => {
   const [isLoggedIn, setIsLoggedIn] = useState(!!api.getAuthToken());
   const [password, setPassword] = useState('');
   const [activeTab, setActiveTab] = useState('videos');
   const [videos, setVideos] = useState([]);
   const [events, setEvents] = useState([]);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState('');

   // Form states
   const [videoForm, setVideoForm] = useState({
      url: '',
      title: '',
      description: '',
      performed_on: '',
      category: '',
   });

   const [eventForm, setEventForm] = useState({
      year: new Date().getFullYear(),
      title: '',
      venue: '',
      date: '',
      time: '',
      subtitle: '',
      image: '',
   });

   const [editingVideo, setEditingVideo] = useState(null);
   const [editingEvent, setEditingEvent] = useState(null);

   // 공연 포스터 업로드 상태
   const [eventFile, setEventFile] = useState(null);     // 새로 선택된 File (없으면 null)
   const [eventPreview, setEventPreview] = useState(null); // 미리보기 URL
   const [uploading, setUploading] = useState(false);
   const [eventFormError, setEventFormError] = useState('');
   const objectUrlRef = useRef(null); // 마지막 생성한 ObjectURL (revoke 대상)
   const fileInputRef = useRef(null);

   // ObjectURL 정리 헬퍼
   const clearObjectUrl = () => {
      if (objectUrlRef.current) {
         URL.revokeObjectURL(objectUrlRef.current);
         objectUrlRef.current = null;
      }
   };

   // 언마운트 시 ObjectURL 해제
   useEffect(() => {
      return () => clearObjectUrl();
   }, []);

   useEffect(() => {
      if (isLoggedIn) {
         loadData();
      }
   }, [isLoggedIn]);

   const loadData = async () => {
      setLoading(true);
      try {
         const [videosData, eventsData] = await Promise.all([
            api.getVideos(),
            api.getEvents(),
         ]);
         setVideos(videosData);
         setEvents(eventsData);
      } catch (err) {
         setError('Failed to load data');
      }
      setLoading(false);
   };

   const handleLogin = async (e) => {
      e.preventDefault();
      setError('');
      const result = await api.login(password);
      if (result.success) {
         setIsLoggedIn(true);
      } else {
         setError('Invalid password');
      }
   };

   const handleLogout = () => {
      api.clearAuthToken();
      setIsLoggedIn(false);
      setPassword('');
   };

   // Video handlers
   const handleVideoSubmit = async (e) => {
      e.preventDefault();
      try {
         if (editingVideo) {
            await api.updateVideo(editingVideo.id, videoForm);
         } else {
            await api.addVideo(videoForm);
         }
         setVideoForm({ url: '', title: '', description: '', performed_on: '', category: '' });
         setEditingVideo(null);
         loadData();
      } catch (err) {
         setError('Failed to save video');
      }
   };

   const handleEditVideo = (video) => {
      setEditingVideo(video);
      setVideoForm({
         url: video.url || '',
         title: video.title || '',
         description: video.description || '',
         performed_on: video.performed_on || '',
         category: video.category || '',
      });
   };

   const handleDeleteVideo = async (id) => {
      if (window.confirm('Are you sure you want to delete this video?')) {
         await api.deleteVideo(id);
         loadData();
      }
   };

   // Event handlers
   const resetEventForm = () => {
      clearObjectUrl();
      setEventForm({
         year: new Date().getFullYear(),
         title: '',
         venue: '',
         date: '',
         time: '',
         subtitle: '',
         image: '',
      });
      setEditingEvent(null);
      setEventFile(null);
      setEventPreview(null);
      setEventFormError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
   };

   // 파일 선택 시: 미리보기 ObjectURL 생성(이전 것 revoke)
   const handleEventFileChange = (e) => {
      const file = e.target.files && e.target.files[0];
      setEventFormError('');
      if (!file) return;
      clearObjectUrl();
      const url = URL.createObjectURL(file);
      objectUrlRef.current = url;
      setEventFile(file);
      setEventPreview(url);
   };

   // 포스터 제거: image 를 빈 문자열로, 미리보기/선택 파일 초기화
   const handleRemovePoster = () => {
      clearObjectUrl();
      setEventFile(null);
      setEventPreview(null);
      setEventForm((prev) => ({ ...prev, image: '' }));
      if (fileInputRef.current) fileInputRef.current.value = '';
   };

   const handleEventSubmit = async (e) => {
      e.preventDefault();
      setEventFormError('');
      let imageValue = eventForm.image;

      // 새 파일이 선택돼 있으면 축소 → 업로드 → url 확보
      if (eventFile) {
         setUploading(true);
         try {
            const resized = await resizeImage(eventFile);
            const result = await api.uploadImage(resized);
            imageValue = result.url;
         } catch (err) {
            setUploading(false);
            setEventFormError(err.message || '이미지 업로드에 실패했습니다');
            return; // 업로드 실패 시 이벤트 저장 중단
         }
         setUploading(false);
      }

      try {
         const payload = { ...eventForm, image: imageValue };
         if (editingEvent) {
            await api.updateEvent(editingEvent.id, payload);
         } else {
            await api.addEvent(payload);
         }
         resetEventForm();
         loadData();
      } catch (err) {
         setEventFormError('공연 저장에 실패했습니다');
      }
   };

   const handleEditEvent = (event) => {
      clearObjectUrl();
      setEditingEvent(event);
      setEventForm({
         year: event.year,
         title: event.title,
         venue: event.venue,
         date: event.date,
         time: event.time || '',
         subtitle: event.subtitle || '',
         image: event.image || '',
      });
      setEventFile(null);
      setEventFormError('');
      // 기존 image 가 표시 가능한 값이면 미리보기
      setEventPreview(resolveEventImage(event.image) || null);
      if (fileInputRef.current) fileInputRef.current.value = '';
   };

   const handleDeleteEvent = async (id) => {
      if (window.confirm('Are you sure you want to delete this event?')) {
         await api.deleteEvent(id);
         loadData();
      }
   };

   // 기존 영상들의 카테고리 목록(중복 제거) — 입력 자동완성(datalist)용
   const videoCategories = [...new Set(videos.map((v) => v.category).filter(Boolean))].sort();

   // Login Screen
   if (!isLoggedIn) {
      return (
         <div className="admin-login">
            <motion.div
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               className="login-card"
            >
               <h1>Admin Login</h1>
               <form onSubmit={handleLogin}>
                  <input
                     type="password"
                     placeholder="Enter password"
                     value={password}
                     onChange={(e) => setPassword(e.target.value)}
                  />
                  {error && <p className="error">{error}</p>}
                  <button type="submit">Login</button>
               </form>
            </motion.div>
         </div>
      );
   }

   return (
      <div className="admin-page">
         {/* Header */}
         <div className="admin-header">
            <h1>Admin Dashboard</h1>
            <button onClick={handleLogout} className="logout-btn">
               Logout
            </button>
         </div>

         {/* Tabs */}
         <div className="admin-tabs">
            <button
               className={`tab ${activeTab === 'videos' ? 'active' : ''}`}
               onClick={() => setActiveTab('videos')}
            >
               Videos ({videos.length})
            </button>
            <button
               className={`tab ${activeTab === 'events' ? 'active' : ''}`}
               onClick={() => setActiveTab('events')}
            >
               Events ({events.length})
            </button>
         </div>

         {loading && <div className="loading">Loading...</div>}
         {error && <div className="error-banner">{error}</div>}

         {/* Videos Tab */}
         {activeTab === 'videos' && (
            <div className="admin-content">
               <div className="form-section">
                  <h2>{editingVideo ? 'Edit Video' : 'Add New Video'}</h2>
                  <form onSubmit={handleVideoSubmit}>
                     <div className="form-group">
                        <label>YouTube URL</label>
                        <input
                           type="text"
                           placeholder="https://www.youtube.com/embed/..."
                           value={videoForm.url}
                           onChange={(e) => setVideoForm({ ...videoForm, url: e.target.value })}
                           required
                        />
                        <small>Use embed URL (e.g., https://www.youtube.com/embed/VIDEO_ID)</small>
                     </div>
                     <div className="form-group">
                        <label>Title</label>
                        <input
                           type="text"
                           placeholder="Video title"
                           value={videoForm.title}
                           onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                           required
                        />
                     </div>
                     <div className="form-row">
                        <div className="form-group">
                           <label>연주일 (선택)</label>
                           <input
                              type="date"
                              value={videoForm.performed_on}
                              onChange={(e) => setVideoForm({ ...videoForm, performed_on: e.target.value })}
                           />
                           <small>비워두면 목록 맨 뒤로 정렬됩니다.</small>
                        </div>
                        <div className="form-group">
                           <label>Category (선택)</label>
                           <input
                              type="text"
                              list="video-category-options"
                              placeholder="e.g., Classical"
                              value={videoForm.category}
                              onChange={(e) => setVideoForm({ ...videoForm, category: e.target.value })}
                           />
                           <datalist id="video-category-options">
                              {videoCategories.map((c) => (
                                 <option key={c} value={c} />
                              ))}
                           </datalist>
                        </div>
                     </div>
                     <div className="form-group">
                        <label>Venue</label>
                        <input
                           type="text"
                           placeholder="Venue (e.g., Sejong Center Chamber Hall)"
                           value={videoForm.description}
                           onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                        />
                     </div>
                     <div className="form-actions">
                        <button type="submit" className="btn-primary">
                           {editingVideo ? 'Update' : 'Add'} Video
                        </button>
                        {editingVideo && (
                           <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => {
                                 setEditingVideo(null);
                                 setVideoForm({ url: '', title: '', description: '', performed_on: '', category: '' });
                              }}
                           >
                              Cancel
                           </button>
                        )}
                     </div>
                  </form>
               </div>

               <div className="list-section">
                  <h2>All Videos</h2>
                  <div className="item-list">
                     {videos.map((video) => (
                        <div key={video.id} className="item-card">
                           <div className="item-info">
                              {video.category && <span className="item-category">{video.category}</span>}
                              <h3>{video.title}</h3>
                              <p>
                                 {video.performed_on ? video.performed_on : '연주일 없음'}
                                 {video.description ? ` | ${video.description}` : ''}
                              </p>
                           </div>
                           <div className="item-actions">
                              <button onClick={() => handleEditVideo(video)} className="btn-edit">
                                 Edit
                              </button>
                              <button onClick={() => handleDeleteVideo(video.id)} className="btn-delete">
                                 Delete
                              </button>
                           </div>
                        </div>
                     ))}
                  </div>
               </div>
            </div>
         )}

         {/* Events Tab */}
         {activeTab === 'events' && (
            <div className="admin-content">
               <div className="form-section">
                  <h2>{editingEvent ? 'Edit Event' : 'Add New Event'}</h2>
                  <form onSubmit={handleEventSubmit}>
                     {eventFormError && <p className="error">{eventFormError}</p>}
                     <div className="form-row">
                        <div className="form-group">
                           <label>Year</label>
                           <input
                              type="number"
                              value={eventForm.year}
                              onChange={(e) => setEventForm({ ...eventForm, year: parseInt(e.target.value) })}
                              required
                           />
                        </div>
                        <div className="form-group">
                           <label>Date</label>
                           <input
                              type="text"
                              placeholder="e.g., November 22, 2023"
                              value={eventForm.date}
                              onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                              required
                           />
                        </div>
                     </div>
                     <div className="form-group">
                        <label>Title</label>
                        <input
                           type="text"
                           placeholder="Event title"
                           value={eventForm.title}
                           onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                           required
                        />
                     </div>
                     <div className="form-group">
                        <label>Venue</label>
                        <input
                           type="text"
                           placeholder="Venue name"
                           value={eventForm.venue}
                           onChange={(e) => setEventForm({ ...eventForm, venue: e.target.value })}
                           required
                        />
                     </div>
                     <div className="form-row">
                        <div className="form-group">
                           <label>Time (optional)</label>
                           <input
                              type="text"
                              placeholder="e.g., 2:00 PM"
                              value={eventForm.time}
                              onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                           />
                        </div>
                        <div className="form-group">
                           <label>Subtitle (optional)</label>
                           <input
                              type="text"
                              placeholder="e.g., With special guest"
                              value={eventForm.subtitle}
                              onChange={(e) => setEventForm({ ...eventForm, subtitle: e.target.value })}
                           />
                        </div>
                     </div>
                     <div className="form-group">
                        <label>포스터 이미지 (선택)</label>
                        {eventPreview && (
                           <div className="poster-preview">
                              <img src={eventPreview} alt="포스터 미리보기" />
                           </div>
                        )}
                        <input
                           ref={fileInputRef}
                           type="file"
                           accept="image/*"
                           onChange={handleEventFileChange}
                        />
                        <small>JPG·PNG·WebP·GIF, 10MB 이하. 업로드 시 자동으로 WebP로 변환됩니다.</small>
                        {(eventPreview || eventForm.image) && (
                           <button
                              type="button"
                              className="btn-remove-poster"
                              onClick={handleRemovePoster}
                           >
                              포스터 제거
                           </button>
                        )}
                     </div>
                     <div className="form-actions">
                        <button type="submit" className="btn-primary" disabled={uploading}>
                           {uploading
                              ? '업로드 중…'
                              : `${editingEvent ? 'Update' : 'Add'} Event`}
                        </button>
                        {editingEvent && (
                           <button
                              type="button"
                              className="btn-secondary"
                              onClick={resetEventForm}
                              disabled={uploading}
                           >
                              Cancel
                           </button>
                        )}
                     </div>
                  </form>
               </div>

               <div className="list-section">
                  <h2>All Events</h2>
                  <div className="item-list">
                     {events
                        .sort((a, b) => b.year - a.year || b.id - a.id)
                        .map((event) => (
                           <div key={event.id} className="item-card">
                              {resolveEventImage(event.image) && (
                                 <img
                                    src={resolveEventImage(event.image)}
                                    alt={`${event.title} 썸네일`}
                                    className="item-thumb"
                                 />
                              )}
                              <div className="item-info">
                                 <span className="item-category">{event.year}</span>
                                 <h3>{event.title}</h3>
                                 <p>{event.venue} | {event.date}</p>
                                 {event.time && <p className="item-time">{event.time}</p>}
                              </div>
                              <div className="item-actions">
                                 <button onClick={() => handleEditEvent(event)} className="btn-edit">
                                    Edit
                                 </button>
                                 <button onClick={() => handleDeleteEvent(event.id)} className="btn-delete">
                                    Delete
                                 </button>
                              </div>
                           </div>
                        ))}
                  </div>
               </div>
            </div>
         )}
      </div>
   );
};

export default Admin;
